import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import orderBy from 'lodash/orderBy';

import { isChargeLvo, isFixedChargeLvoForUnit, isProvisionedChargeLvoForUnit } from './chargesUtils';
import { removeDeletedItemsInObject } from './databaseUtils';
import {
  CalendarDate,
  toCalendarDate,
  isAfter,
  setCalendarDate,
  isSameMonthAndYear,
  subtractCalendarDate,
  addCalendarDate,
  isBeforeOrEqual,
  areIntervalsOverlapping,
  isBefore,
  isWithinInterval,
  isEqual,
  getMonthsInInterval,
  getNumberOfDaysInInterval,
  isAfterOrEqual,
  toCalendarDateNullable,
  today,
  formatCalendarDate,
  Interval,
  startOfCalendarMonth,
  endOfCalendarMonth,
  getNumberOfMonthsBetweenCalendarDates,
  getNumberOfYearsInInterval,
} from './dateutils';
import { getLastInvoiceRunDate } from './draftInvoiceTimingUtils';
import { getFilterEntityValuesForGroups } from './filterGroupsUtils';
import { roundForFrequencyPrice } from './frequencyAmountRoundingUtils';
import { getEndLeaseActionHistoryToProcess } from './leaseActionHistoryUtils';
import { FLOATING_POINT_TOLERANCE } from './numbers';
import { isNilOrEmpty } from './objectUtils';
import {
  computeInvoicingPeriods,
  getNextPeriodStartDate,
  getNextStartDate,
  getPrevBimestrialStart,
  getStartDayForInvoicePeriod,
  getStartPeriodDay,
} from './periodUtils';
import {
  Building,
  Contact,
  ContactType,
  FilterEntityType,
  FilterGroup,
  Language,
  Lease,
  LeaseAction,
  LeaseActionEndInitiator,
  LeaseActionHistory,
  LeaseActionHistoryStatus,
  LeaseAmountUpdateStatus,
  LeaseDTO,
  LeasePaymentFrequency,
  LeasePaymentInvoicePeriod,
  LeasePriceHistory,
  LeasePriceHistoryDTO,
  LeasePriceHistoryType,
  LeaseStatus,
  LeaseType,
  LeaseVariousOperation,
  LeaseVariousOperationType,
  ModelLeaseContactConnection,
  ModelUnitLeaseConnection,
  PaymentProviderMandateStatus,
  Posting,
  Unit,
  UnitDTO,
  UnitLease,
  UnitLeaseDTO,
} from './types';

export interface LeaseExtended extends Lease {
  tenants: Contact[];
  guarantors: Contact[];
  lessors: Contact[];
}

export const ADVANCED_INDEXATION_LEASE_TYPES = [
  LeaseType.COMMON_LAW,
  LeaseType.WAREHOUSE,
  LeaseType.OFFICE,
  LeaseType.COMMERCIAL,
];

/** On its own documents, a line at these frequencies prorates its first calendar period before the first rent call. */
export const FREQUENCIES_WITH_PARTIAL_CALENDAR_FIRST_PERIOD = [
  LeasePaymentFrequency.YEARLY,
  LeasePaymentFrequency.BI_ANNUAL,
  LeasePaymentFrequency.QUARTERLY,
];

export const FREQUENCY_FOR_CALENDAR_PRO_RATA = [
  LeasePaymentFrequency.YEARLY,
  LeasePaymentFrequency.BI_ANNUAL,
  LeasePaymentFrequency.QUARTERLY,
  LeasePaymentFrequency.MONTHLY,
];

const getLegalLeaseDurationInYears = (leaseType: LeaseType): number | null => {
  switch (leaseType) {
    case LeaseType.COMMERCIAL:
    case LeaseType.MAIN_RESIDENCE_9_YEARS:
    case LeaseType.MAIN_RESIDENCE_LONG:
      return 9;

    case LeaseType.COMMON_LAW:
    case LeaseType.PARKING:
    case LeaseType.OFFICE:
    case LeaseType.WAREHOUSE:
    case LeaseType.STUDENT:
    case LeaseType.MAIN_RESIDENCE_SHORT:
    case LeaseType.FLATSHARING:
      return 1;

    case LeaseType.MAIN_RESIDENCE_INFINITE:
      return 100;

    default:
      return null;
  }
};

export const getLegalLeaseEndDate = (leaseType: LeaseType, startDate: CalendarDate): CalendarDate => {
  const durationInYears = getLegalLeaseDurationInYears(leaseType);
  if (!durationInYears) {
    return startDate;
  }
  return subtractCalendarDate(addCalendarDate(startDate, durationInYears, 'years'), 1, 'days');
};

export const getLeaseIndexationForDate = (
  date?: CalendarDate | null,
  leaseIndexations?: LeasePriceHistoryDTO[] | LeasePriceHistory[]
): null | LeasePriceHistoryDTO => {
  if (isNil(date) || isNil(leaseIndexations) || isEmpty(leaseIndexations)) {
    return null;
  }

  const filteredIndexations = (leaseIndexations as LeasePriceHistoryDTO[]).filter(
    (leaseIndexation) =>
      leaseIndexation.status === LeaseAmountUpdateStatus.APPLIED &&
      leaseIndexation.type === LeasePriceHistoryType.INDEXATION &&
      leaseIndexation.applicationDate
  );

  if (isEmpty(filteredIndexations)) {
    return null;
  }

  return filteredIndexations.reduce(
    (latestIndexation: LeasePriceHistoryDTO | null, currentIndexation: LeasePriceHistoryDTO) => {
      const currentIndexationDate = toCalendarDate(currentIndexation.applicationDate);

      if (!isBeforeOrEqual(currentIndexationDate, date)) {
        return latestIndexation;
      }

      if (isNil(latestIndexation)) {
        return currentIndexation;
      }

      const latestIndexationDate = toCalendarDate(latestIndexation.applicationDate);

      return isAfter(currentIndexationDate, latestIndexationDate) ? currentIndexation : latestIndexation;
    },
    null
  );
};

/**
 * At the given date, is it the first invoice that will be done.
 */
export const isLeaseFirstInvoice = (
  startDate: CalendarDate,
  endDate: CalendarDate,
  lastInvoiceDate: CalendarDate | undefined | null,
  mainUnit: boolean,
  invoicePeriodFrom: CalendarDate,
  invoicePeriodTo: CalendarDate
): boolean => {
  const dateInLeaseInterval = areIntervalsOverlapping(
    { start: startDate, end: endDate },
    { start: invoicePeriodFrom, end: invoicePeriodTo }
  );

  const startDateInCurrentPeriod = isWithinInterval(startDate, {
    start: invoicePeriodFrom,
    end: invoicePeriodTo,
  });

  if (!dateInLeaseInterval || !startDateInCurrentPeriod) {
    return false;
  }

  return !mainUnit || isNil(lastInvoiceDate) || isBefore(lastInvoiceDate, startDate);
};

/**
 * At the given invoiceDate, is it the last invoice that will be done.
 */
export const isLeaseLastInvoice = (
  endDate: CalendarDate,
  lastInvoiceDate: CalendarDate | null | undefined,
  invoiceDate: CalendarDate
): boolean => {
  if (isNil(lastInvoiceDate)) {
    return false;
  }
  const nextInvoiceMonth = invoiceDate;

  // Is in interval and same month as lease end
  return isSameMonthAndYear(nextInvoiceMonth, endDate) || isAfter(nextInvoiceMonth, endDate);
};

export const isDateMatchingCustomDay = (lastDay: number, periodDay: number, customDay: number): boolean => {
  const effectiveCustomDay = customDay > lastDay ? lastDay : customDay;
  return periodDay !== effectiveCustomDay;
};

export const isDateMatchingCustomDayEnd = (lastDay: number, periodDay: number, customDay: number): boolean => {
  if (customDay === 1) {
    return periodDay !== lastDay;
  }

  const expectedEndDay = customDay - 1;

  if (expectedEndDay > lastDay) {
    return periodDay !== lastDay;
  }

  return periodDay !== expectedEndDay;
};

export const doesLeaseNeedProRataForFirstPeriod = (
  startDate: CalendarDate,
  paymentInvoicePeriod: LeasePaymentInvoicePeriod,
  paymentFrequency: LeasePaymentFrequency,
  paymentInvoicePeriodCustomDay: number | null | undefined
): boolean => {
  if (paymentInvoicePeriod === LeasePaymentInvoicePeriod.CUSTOM_PERIOD && paymentInvoicePeriodCustomDay) {
    return startDate.day !== paymentInvoicePeriodCustomDay;
  }
  const startPeriodDay = getStartPeriodDay(
    startDate,
    paymentInvoicePeriod,
    paymentInvoicePeriodCustomDay ?? undefined,
    paymentFrequency
  );

  return !isEqual(startPeriodDay, startDate);
};

export const doesLeaseNeedProRataForLastPeriod = (
  startDate: CalendarDate,
  endDate: CalendarDate,
  paymentInvoicePeriod: LeasePaymentInvoicePeriod,
  paymentFrequency: LeasePaymentFrequency,
  paymentInvoicePeriodCustomDay: number | null | undefined
): boolean => {
  if (paymentInvoicePeriod === LeasePaymentInvoicePeriod.CONTRACT_PERIOD) {
    return false;
  }

  const periods = computeInvoicingPeriods(
    startDate,
    endDate,
    paymentInvoicePeriod,
    paymentFrequency,
    paymentInvoicePeriodCustomDay ?? undefined
  );

  const lastPeriod = periods[periods.length - 1];

  const nextStart = getNextPeriodStartDate(
    lastPeriod.periodFrom,
    paymentInvoicePeriod,
    paymentInvoicePeriodCustomDay ?? undefined,
    paymentFrequency
  );

  const endPeriod = subtractCalendarDate(nextStart, 1, 'days');

  return !isEqual(endPeriod, endDate);
};
/**
 * Depending on lease config, the first and last invoice month will need pro rata
 */
export const doesLeaseNeedProRataForFirstAndLastPeriod = (
  isFirstInvoice: boolean,
  isLastInvoice: boolean,
  startDate: CalendarDate,
  endDate: CalendarDate,
  paymentInvoicePeriod: LeasePaymentInvoicePeriod,
  paymentFrequency: LeasePaymentFrequency,
  paymentInvoicePeriodCustomDay: number | null | undefined
): boolean => {
  if (isFirstInvoice) {
    return doesLeaseNeedProRataForFirstPeriod(
      startDate,
      paymentInvoicePeriod,
      paymentFrequency,
      paymentInvoicePeriodCustomDay
    );
  }
  if (isLastInvoice) {
    return doesLeaseNeedProRataForLastPeriod(
      startDate,
      endDate,
      paymentInvoicePeriod,
      paymentFrequency,
      paymentInvoicePeriodCustomDay
    );
  }

  return false;
};

const getDaysInFirstPartialMonth = (date: CalendarDate): number => date.daysInMonth - date.day + 1;

const getDaysInLastPartialMonth = (date: CalendarDate): number => date.day;

const getFirstDayOfNextMonth = (date: CalendarDate): CalendarDate =>
  startOfCalendarMonth(addCalendarDate(date, 1, 'months'));

const calculateMonthBreakdown = (
  periodFrom: CalendarDate,
  periodTo: CalendarDate,
  startsOnFirstDay: boolean,
  endsOnLastDay: boolean
): { fullMonths: number; partialDays: number; partialTotalDays: number } => {
  if (startsOnFirstDay && endsOnLastDay) {
    return {
      fullMonths: getMonthsInInterval({ start: periodFrom, end: periodTo }).length,
      partialDays: 0,
      partialTotalDays: 0,
    };
  }

  if (isSameMonthAndYear(periodFrom, periodTo)) {
    return {
      fullMonths: 0,
      partialDays: periodTo.day - periodFrom.day + 1,
      partialTotalDays: periodFrom.daysInMonth,
    };
  }

  const partialDaysAtStart = startsOnFirstDay ? 0 : getDaysInFirstPartialMonth(periodFrom);
  const partialTotalDaysAtStart = startsOnFirstDay ? 0 : periodFrom.daysInMonth;

  const partialDaysAtEnd = endsOnLastDay ? 0 : getDaysInLastPartialMonth(periodTo);
  const partialTotalDaysAtEnd = endsOnLastDay ? 0 : periodTo.daysInMonth;

  const firstFullMonth = startsOnFirstDay ? periodFrom : getFirstDayOfNextMonth(periodFrom);

  const lastFullMonth = endsOnLastDay ? periodTo : subtractCalendarDate(startOfCalendarMonth(periodTo), 1, 'months');

  return {
    fullMonths: getMonthsInInterval({
      start: firstFullMonth,
      end: lastFullMonth,
    }).length,
    partialDays: partialDaysAtStart + partialDaysAtEnd,
    partialTotalDays: partialTotalDaysAtStart + partialTotalDaysAtEnd,
  };
};

export interface ProRataBreakdown {
  fullMonths: number;
  partialDays: number;
  partialMonthRatio: number;
  totalMonthsCovered: number;
}

export const calculateProRataBreakdown = (
  invoicePeriodFrom: CalendarDate,
  invoicePeriodTo: CalendarDate,
  paymentFrequency: LeasePaymentFrequency,
  paymentInvoicePeriod?: LeasePaymentInvoicePeriod,
  paymentInvoicePeriodCustomDay?: number | null
): ProRataBreakdown => {
  const totalMonths = getNumberOfMonthsFromFrequency(paymentFrequency);

  if (
    paymentInvoicePeriod === LeasePaymentInvoicePeriod.CUSTOM_PERIOD &&
    paymentInvoicePeriodCustomDay &&
    totalMonths > 1
  ) {
    const customDay = paymentInvoicePeriodCustomDay;

    const startsOnCustomDay = invoicePeriodFrom.day === customDay;
    const expectedEndDay = customDay === 1 ? invoicePeriodTo.daysInMonth : customDay - 1;
    const endsOnLastDayForCustomDay1 = customDay === 1 && invoicePeriodTo.day === invoicePeriodTo.daysInMonth;
    const endsOnCustomDayMinusOne = invoicePeriodTo.day === expectedEndDay || endsOnLastDayForCustomDay1;

    if (startsOnCustomDay && endsOnCustomDayMinusOne) {
      return { fullMonths: totalMonths, partialDays: 0, partialMonthRatio: 0, totalMonthsCovered: totalMonths };
    }

    const firstCustomDayCandidate = setCalendarDate(
      invoicePeriodFrom,
      Math.min(customDay, invoicePeriodFrom.daysInMonth),
      'day'
    );

    const firstCustomDayReferenceDate = isBefore(firstCustomDayCandidate, invoicePeriodFrom)
      ? addCalendarDate(invoicePeriodFrom, 1, 'months')
      : invoicePeriodFrom;

    const firstCustomDayInPeriod = setCalendarDate(
      firstCustomDayReferenceDate,
      Math.min(customDay, firstCustomDayReferenceDate.daysInMonth),
      'day'
    );

    const getCustomPeriodEndDay = (date: CalendarDate): CalendarDate => {
      if (customDay === 1) {
        return endOfCalendarMonth(date);
      }
      const endDay = Math.min(customDay - 1, date.daysInMonth);
      return setCalendarDate(date, endDay, 'day');
    };

    let lastCustomDayEndInPeriod = getCustomPeriodEndDay(invoicePeriodTo);
    if (isAfter(lastCustomDayEndInPeriod, invoicePeriodTo)) {
      lastCustomDayEndInPeriod = getCustomPeriodEndDay(subtractCalendarDate(invoicePeriodTo, 1, 'months'));
    }

    let fullMonths = 0;
    if (!isAfter(firstCustomDayInPeriod, lastCustomDayEndInPeriod)) {
      const nextBoundary = addCalendarDate(lastCustomDayEndInPeriod, 1, 'days');
      fullMonths = getNumberOfMonthsBetweenCalendarDates(firstCustomDayInPeriod, nextBoundary);
    }

    let partialDays = 0;
    const partialTotalDays = 30;

    if (isBefore(invoicePeriodFrom, firstCustomDayInPeriod)) {
      partialDays += getNumberOfDaysInInterval({ start: invoicePeriodFrom, end: firstCustomDayInPeriod }, false);
    }

    if (isAfter(invoicePeriodTo, lastCustomDayEndInPeriod) && fullMonths > 0) {
      partialDays += getNumberOfDaysInInterval({ start: lastCustomDayEndInPeriod, end: invoicePeriodTo }, false);
    }

    if (fullMonths === 0) {
      partialDays = getNumberOfDaysInInterval({ start: invoicePeriodFrom, end: invoicePeriodTo });
    }

    const partialMonthRatio = partialDays > 0 ? partialDays / partialTotalDays : 0;
    const totalMonthsCovered = fullMonths + partialMonthRatio;

    return { fullMonths, partialDays, partialMonthRatio, totalMonthsCovered };
  }

  if (totalMonths <= 1) {
    const nbDaysInInvoice = getNumberOfDaysInInterval({ start: invoicePeriodFrom, end: invoicePeriodTo });
    const daysInMonth = invoicePeriodFrom.daysInMonth;
    const ratio = nbDaysInInvoice / daysInMonth;
    if (nbDaysInInvoice === daysInMonth) {
      return { fullMonths: 1, partialDays: 0, partialMonthRatio: 0, totalMonthsCovered: 1 };
    }
    return { fullMonths: 0, partialDays: nbDaysInInvoice, partialMonthRatio: ratio, totalMonthsCovered: ratio };
  }

  const startsOnFirstDay = invoicePeriodFrom.day === 1;
  const endsOnLastDay = invoicePeriodTo.day === invoicePeriodTo.daysInMonth;

  const { fullMonths, partialDays, partialTotalDays } = calculateMonthBreakdown(
    invoicePeriodFrom,
    invoicePeriodTo,
    startsOnFirstDay,
    endsOnLastDay
  );

  const partialMonthRatio = partialDays > 0 ? partialDays / partialTotalDays : 0;
  const totalMonthsCovered = fullMonths + partialMonthRatio;

  return { fullMonths, partialDays, partialMonthRatio, totalMonthsCovered };
};

export const calculateHybridProRataCoefficient = (
  invoicePeriodFrom: CalendarDate,
  invoicePeriodTo: CalendarDate,
  paymentFrequency: LeasePaymentFrequency
): number => {
  const breakdown = calculateProRataBreakdown(invoicePeriodFrom, invoicePeriodTo, paymentFrequency);
  return breakdown.totalMonthsCovered;
};

export const calculateCalendarPeriodProRata = (
  paymentFrequency: LeasePaymentFrequency,
  operationStart: CalendarDate,
  calendarPeriodEnd: CalendarDate
) => {
  const monthsCovered = calculateHybridProRataCoefficient(operationStart, calendarPeriodEnd, paymentFrequency);
  const totalMonthsInPeriod = getNumberOfMonthsFromFrequency(paymentFrequency);

  const isFullPeriod = Math.abs(monthsCovered - totalMonthsInPeriod) < FLOATING_POINT_TOLERANCE;
  const proRataCoefficient = isFullPeriod ? 1 : monthsCovered / totalMonthsInPeriod;

  return {
    postingPeriodFrom: operationStart,
    postingPeriodTo: calendarPeriodEnd,
    proRataCoefficient,
    numberOfFullMonths: 0,
  };
};

export interface ProRataResult {
  fullMonths: number;
  partialMonthRatio: number;
}

export const getProRataForInvoice = (
  startDate: CalendarDate,
  endDate: CalendarDate,
  lastInvoiceDate: CalendarDate | null | undefined,
  paymentInvoicePeriod: LeasePaymentInvoicePeriod,
  paymentFrequency: LeasePaymentFrequency,
  paymentInvoicePeriodCustomDay: number | null | undefined,
  mainUnit: boolean,
  dateOfInvoice: CalendarDate,
  invoicePeriodFrom: CalendarDate,
  invoicePeriodTo: CalendarDate
): ProRataResult | undefined => {
  const isFirstInvoice = isLeaseFirstInvoice(
    startDate,
    endDate,
    lastInvoiceDate,
    mainUnit,
    invoicePeriodFrom,
    invoicePeriodTo
  );
  const isLastInvoice = isLeaseLastInvoice(endDate, lastInvoiceDate, dateOfInvoice);
  const needsProRataAndIsFirstOrLastMonth = doesLeaseNeedProRataForFirstAndLastPeriod(
    isFirstInvoice,
    isLastInvoice,
    startDate,
    endDate,
    paymentInvoicePeriod as LeasePaymentInvoicePeriod,
    paymentFrequency as LeasePaymentFrequency,
    paymentInvoicePeriodCustomDay
  );
  if (!needsProRataAndIsFirstOrLastMonth) {
    return undefined;
  }

  const breakdown = calculateProRataBreakdown(
    invoicePeriodFrom,
    invoicePeriodTo,
    paymentFrequency,
    paymentInvoicePeriod,
    paymentInvoicePeriodCustomDay
  );

  return {
    fullMonths: breakdown.fullMonths,
    partialMonthRatio: breakdown.partialMonthRatio,
  };
};

export const getPeriodsAndProRataCoefficientForUnitLease = (
  unitLeaseStartDate: CalendarDate,
  unitLeaseEndDate: CalendarDate,
  mainUnit: boolean,
  lastInvoiceDate: CalendarDate | undefined | null,
  paymentFrequency: LeasePaymentFrequency,
  paymentInvoicePeriod: LeasePaymentInvoicePeriod,
  paymentInvoicePeriodCustomDay: number | undefined | null,
  dateOfInvoice: CalendarDate,
  periodFrom: CalendarDate,
  periodTo: CalendarDate,
  discardProRata: boolean = false
): {
  postingPeriodFrom: CalendarDate;
  postingPeriodTo: CalendarDate;
  proRataCoefficient: number | undefined;
  numberOfFullMonths: number;
} => {
  let postingPeriodFrom = periodFrom;
  let postingPeriodTo = periodTo;

  const isFirstInvoice = isLeaseFirstInvoice(
    unitLeaseStartDate,
    unitLeaseEndDate,
    lastInvoiceDate,
    mainUnit,
    periodFrom,
    periodTo
  );

  const isLastInvoice = isLeaseLastInvoice(unitLeaseEndDate, lastInvoiceDate, dateOfInvoice);

  const leaseStartsAfterPeriodStart = isAfter(unitLeaseStartDate, periodFrom);

  if (isFirstInvoice && !discardProRata && leaseStartsAfterPeriodStart) {
    postingPeriodFrom = unitLeaseStartDate;
  }

  const leaseEndsBeforePeriodEnd = isBefore(unitLeaseEndDate, periodTo);

  if (isLastInvoice && !discardProRata && leaseEndsBeforePeriodEnd) {
    postingPeriodTo = unitLeaseEndDate;
  }

  const proRataResult = getProRataForInvoice(
    unitLeaseStartDate,
    unitLeaseEndDate,
    lastInvoiceDate,
    paymentInvoicePeriod,
    paymentFrequency,
    paymentInvoicePeriodCustomDay,
    mainUnit,
    dateOfInvoice,
    postingPeriodFrom,
    postingPeriodTo
  );

  const numberOfFullMonths = proRataResult
    ? proRataResult.fullMonths
    : getNumberOfMonthsFromFrequency(paymentFrequency);

  const proRataCoefficient = proRataResult?.partialMonthRatio;

  return {
    postingPeriodFrom,
    postingPeriodTo,
    proRataCoefficient,
    numberOfFullMonths,
  };
};

export const isLeaseLastInvoiceAlreadySentAndCompensationNeedsToBeInvoiced = (
  lease: Lease,
  date: CalendarDate,
  daysBeforeDueDateSetting: number,
  leaseActionHistories: LeaseActionHistory[]
): boolean => {
  const leaseStartDate = toCalendarDate(lease.startDate);
  const leaseEndDate = toCalendarDate(lease.endDate);

  const firstLeaseInvoiceDate = subtractCalendarDate(leaseStartDate, daysBeforeDueDateSetting, 'days');

  const lastLeaseInvoiceDate = endOfCalendarMonth(leaseEndDate);

  const dateInLeaseInterval = isWithinInterval(date, {
    start: firstLeaseInvoiceDate,
    end: lastLeaseInvoiceDate,
  });

  if (dateInLeaseInterval || isNil(lease.lastInvoiceDate)) {
    return false;
  }

  const lastMonthInvoiced = toCalendarDate(lease.lastInvoiceDate);

  if (!isSameMonthAndYear(lastMonthInvoiced, leaseEndDate)) {
    return false;
  }

  const hasActionThatIsNotYetProcessed = getEndLeaseActionHistoryToProcess(leaseActionHistories, lease.id);

  return !isNil(hasActionThatIsNotYetProcessed);
};

export const MAX_DRAFT_LEAD_DAYS = 30;

export enum InvoiceTimingPhase {
  DRAFT = 'DRAFT',
  FINALIZE = 'FINALIZE',
}

// The next period is gated on the later of the issued and drafted dates, since either can lead:
// lastDraftInvoiceDate while a draft awaits finalization, lastInvoiceDate after a zero-amount period (no draft)
export const getLatestInvoicedOrDraftedDate = (
  lastInvoiceDate: string | null | undefined,
  lastDraftInvoiceDate: string | null | undefined
): string | null | undefined => {
  if (isNil(lastDraftInvoiceDate)) {
    return lastInvoiceDate;
  }
  if (isNil(lastInvoiceDate)) {
    return lastDraftInvoiceDate;
  }
  return isAfter(toCalendarDate(lastDraftInvoiceDate), toCalendarDate(lastInvoiceDate))
    ? lastDraftInvoiceDate
    : lastInvoiceDate;
};

export const hasPendingDraftInvoice = (
  lastInvoiceDate: string | null | undefined,
  lastDraftInvoiceDate: string | null | undefined
): boolean => {
  if (isNil(lastDraftInvoiceDate)) {
    return false;
  }
  return isNil(lastInvoiceDate) || isAfter(toCalendarDate(lastDraftInvoiceDate), toCalendarDate(lastInvoiceDate));
};

const NO_INDEMNITY_MONTHS = 0;
const SHORT_TERMS_INDEMNITY_MONTHS = 1;
const COMMERCIAL_INDEMNITY_MONTHS = 6;
const TENANT_LONG_TERM_INDEMNITY_MONTHS_BY_LEASE_YEAR = [3, 2, 1];
const FLANDERS_SHORT_TERMS_TENANT_INDEMNITY_MONTHS_BY_LEASE_YEAR = [1.5, 1, 0.5];
const LESSOR_FIRST_TRIENNIUM_INDEMNITY_MONTHS = 9;
const LESSOR_SECOND_TRIENNIUM_INDEMNITY_MONTHS = 6;
const LESSOR_EARLY_INDEMNITY_MONTHS = 3;
const FIRST_TRIENNIUM_YEARS = 3;
const SECOND_TRIENNIUM_YEARS = 6;
const THIRD_TRIENNIUM_YEARS = 9;

export const getNumberOfMonthsForIndemnity = (
  initiator: LeaseActionEndInitiator,
  isShortTerms: boolean,
  isCommercial: boolean,
  isFlanders: boolean,
  startDate: CalendarDate,
  previousEndDate: CalendarDate,
  newEndDate: CalendarDate
): number => {
  if (
    initiator === LeaseActionEndInitiator.MUTUAL ||
    isEqual(previousEndDate, newEndDate) ||
    isBefore(newEndDate, startDate)
  ) {
    return NO_INDEMNITY_MONTHS;
  }

  const isTenantEnding = initiator === LeaseActionEndInitiator.TENANT;
  const isLessorEnding = initiator === LeaseActionEndInitiator.LESSOR;
  const leaseYearOfEnd = getNumberOfYearsInInterval({ start: startDate, end: newEndDate }) + 1;

  if (isTenantEnding && isShortTerms) {
    if (!isFlanders) {
      return SHORT_TERMS_INDEMNITY_MONTHS;
    }
    const flandersLeaseYearIndex =
      Math.min(leaseYearOfEnd, FLANDERS_SHORT_TERMS_TENANT_INDEMNITY_MONTHS_BY_LEASE_YEAR.length) - 1;

    return FLANDERS_SHORT_TERMS_TENANT_INDEMNITY_MONTHS_BY_LEASE_YEAR[flandersLeaseYearIndex];
  }

  if (isLessorEnding && isShortTerms) {
    return SHORT_TERMS_INDEMNITY_MONTHS;
  }

  if (isCommercial) {
    return COMMERCIAL_INDEMNITY_MONTHS;
  }

  if (isTenantEnding) {
    if (leaseYearOfEnd > TENANT_LONG_TERM_INDEMNITY_MONTHS_BY_LEASE_YEAR.length) {
      return NO_INDEMNITY_MONTHS;
    }

    return TENANT_LONG_TERM_INDEMNITY_MONTHS_BY_LEASE_YEAR[leaseYearOfEnd - 1];
  }

  if (isLessorEnding) {
    const completedLeaseYears = getNumberOfYearsInInterval({
      start: startDate,
      end: addCalendarDate(newEndDate, 1, 'days'),
    });
    if (completedLeaseYears >= FIRST_TRIENNIUM_YEARS && completedLeaseYears < SECOND_TRIENNIUM_YEARS) {
      return LESSOR_FIRST_TRIENNIUM_INDEMNITY_MONTHS;
    }
    if (completedLeaseYears >= SECOND_TRIENNIUM_YEARS && completedLeaseYears < THIRD_TRIENNIUM_YEARS) {
      return LESSOR_SECOND_TRIENNIUM_INDEMNITY_MONTHS;
    }

    return LESSOR_EARLY_INDEMNITY_MONTHS;
  }

  return NO_INDEMNITY_MONTHS;
};

export const doesLeaseNeedToBeInvoiced = (
  lease: Lease,
  date: CalendarDate,
  daysBeforeDueDateSetting: number,
  invoiceDaysAfterPeriodStart?: number | null,
  phase: InvoiceTimingPhase = InvoiceTimingPhase.FINALIZE
) =>
  needsToBeInvoiced(
    date,
    toCalendarDate(lease.startDate),
    toCalendarDate(lease.endDate),
    toCalendarDateNullable(getLatestInvoicedOrDraftedDate(lease.lastInvoiceDate, lease.lastDraftInvoiceDate)),
    lease.paymentFrequency,
    lease.paymentInvoicePeriod,
    lease.paymentInvoicePeriodCustomDay,
    daysBeforeDueDateSetting,
    invoiceDaysAfterPeriodStart,
    phase,
    hasPendingDraftInvoice(lease.lastInvoiceDate, lease.lastDraftInvoiceDate)
  );

export const doesLeaseAdvancedChargesNeedToBeInvoiced = (
  lease: Lease,
  date: CalendarDate,
  daysBeforeDueDateSetting: number,
  invoiceDaysAfterPeriodStart?: number | null,
  phase: InvoiceTimingPhase = InvoiceTimingPhase.FINALIZE
) => {
  if (!lease.advancedChargesInvoices) {
    return false;
  }

  return needsToBeInvoiced(
    date,
    toCalendarDate(lease.startDate),
    toCalendarDate(lease.endDate),
    toCalendarDateNullable(
      getLatestInvoicedOrDraftedDate(
        lease.advancedChargesInvoices.lastInvoiceDate,
        lease.advancedChargesInvoices.lastDraftInvoiceDate
      )
    ),
    lease.advancedChargesInvoices.paymentFrequency,
    lease.advancedChargesInvoices.paymentInvoicePeriod,
    lease.advancedChargesInvoices.paymentInvoicePeriodCustomDay,
    daysBeforeDueDateSetting,
    invoiceDaysAfterPeriodStart,
    phase,
    hasPendingDraftInvoice(
      lease.advancedChargesInvoices.lastInvoiceDate,
      lease.advancedChargesInvoices.lastDraftInvoiceDate
    )
  );
};

export type LeaseVariousOperationInvoicingFields = Pick<
  LeaseVariousOperation,
  | 'startDate'
  | 'endDate'
  | 'lastInvoiceDate'
  | 'paymentFrequency'
  | 'paymentInvoicePeriod'
  | 'paymentInvoicePeriodCustomDay'
>;

export const doesLeaseVariousOperationNeedsToBeInvoiced = (
  leaseVariousOperation: LeaseVariousOperationInvoicingFields,
  date: CalendarDate,
  daysBeforeDueDateSetting: number = 0,
  invoiceDaysAfterPeriodStart: number | null = null
) => {
  const startDate = toCalendarDate(leaseVariousOperation.startDate);
  const lastInvoiceDate = toCalendarDateNullable(leaseVariousOperation.lastInvoiceDate);
  const isDue = needsToBeInvoiced(
    date,
    startDate,
    toCalendarDate(leaseVariousOperation.endDate),
    lastInvoiceDate,
    leaseVariousOperation.paymentFrequency,
    leaseVariousOperation.paymentInvoicePeriod,
    leaseVariousOperation.paymentInvoicePeriodCustomDay,
    daysBeforeDueDateSetting,
    invoiceDaysAfterPeriodStart
  );
  if (!isDue || !isNil(lastInvoiceDate) || !invoiceDaysAfterPeriodStart) {
    return isDue;
  }
  const { invoiceDate: firstSendDate } = computeInvoiceDates(
    startDate,
    invoiceDaysAfterPeriodStart,
    daysBeforeDueDateSetting
  );
  return isAfterOrEqual(date, firstSendDate);
};

const getDraftLeadDays = (
  startDate: CalendarDate,
  endDate: CalendarDate,
  lastInvoiceDate: CalendarDate | null | undefined,
  paymentFrequency: LeasePaymentFrequency | keyof typeof LeasePaymentFrequency | undefined,
  paymentInvoicePeriod: LeasePaymentInvoicePeriod | keyof typeof LeasePaymentInvoicePeriod | undefined,
  paymentInvoicePeriodCustomDay: number | null | undefined
): number => {
  const [nextPeriod] = computeInvoicingPeriods(
    startDate,
    endDate,
    paymentInvoicePeriod as LeasePaymentInvoicePeriod,
    paymentFrequency as LeasePaymentFrequency,
    paymentInvoicePeriodCustomDay ?? undefined,
    lastInvoiceDate ?? undefined,
    1
  );
  if (isNil(nextPeriod)) {
    return MAX_DRAFT_LEAD_DAYS;
  }
  return Math.min(
    MAX_DRAFT_LEAD_DAYS,
    getNumberOfDaysInInterval({ start: nextPeriod.periodFrom, end: nextPeriod.periodTo })
  );
};

// A draft that becomes due after the last run fetching its lease would never be created
const capDraftDateToLastRun = (
  sendDate: CalendarDate,
  endDate: CalendarDate,
  phase: InvoiceTimingPhase
): CalendarDate => {
  const lastRunDate = getLastInvoiceRunDate(endDate);
  return phase === InvoiceTimingPhase.DRAFT && isAfter(sendDate, lastRunDate) ? lastRunDate : sendDate;
};

const needsToBeInvoiced = (
  date: CalendarDate,
  startDate: CalendarDate,
  endDate: CalendarDate,
  lastInvoiceDate: CalendarDate | null | undefined,
  paymentFrequency: LeasePaymentFrequency | keyof typeof LeasePaymentFrequency | undefined,
  paymentInvoicePeriod: LeasePaymentInvoicePeriod | keyof typeof LeasePaymentInvoicePeriod | undefined,
  paymentInvoicePeriodCustomDay: number | null | undefined,
  daysBeforeDueDateSetting: number,
  invoiceDaysAfterPeriodStart?: number | null,
  phase: InvoiceTimingPhase = InvoiceTimingPhase.FINALIZE,
  hasPendingDraft: boolean = false
): boolean => {
  const isDraftLead = phase === InvoiceTimingPhase.DRAFT && !hasPendingDraft;
  const leadDaysBeforeDueDate = isDraftLead
    ? daysBeforeDueDateSetting +
      getDraftLeadDays(
        startDate,
        endDate,
        lastInvoiceDate,
        paymentFrequency,
        paymentInvoicePeriod,
        paymentInvoicePeriodCustomDay
      )
    : daysBeforeDueDateSetting;
  const firstLeaseInvoiceDate = subtractCalendarDate(startDate, leadDaysBeforeDueDate, 'days');

  const lastLeaseInvoiceDate = endOfCalendarMonth(endDate);

  const dateInLeaseInterval = isWithinInterval(date, {
    start: firstLeaseInvoiceDate,
    end: lastLeaseInvoiceDate,
  });

  // Not in interval AND not empty and last month not yet invoiced
  if (!dateInLeaseInterval && !isNil(lastInvoiceDate)) {
    const isOneTimeOperation = isEqual(startDate, endDate);

    const alreadyInvoicedAndNothingMoreToDo =
      isAfter(lastInvoiceDate, endDate) || (isOneTimeOperation && isEqual(lastInvoiceDate, endDate));

    if (isBefore(lastInvoiceDate, date) && !alreadyInvoicedAndNothingMoreToDo) {
      return true;
    }
  }

  // After end date and never invoiced
  if (!dateInLeaseInterval && isAfter(date, endDate) && isNil(lastInvoiceDate)) {
    return true;
  }

  if (!dateInLeaseInterval) {
    return false;
  }

  if (isNil(lastInvoiceDate)) {
    if (!invoiceDaysAfterPeriodStart) {
      return true;
    }

    const effectiveDueDate = computeInvoiceDueDate(startDate, invoiceDaysAfterPeriodStart);

    const firstSendDate = capDraftDateToLastRun(
      subtractCalendarDate(effectiveDueDate, leadDaysBeforeDueDate, 'days'),
      endDate,
      phase
    );

    return isAfterOrEqual(date, firstSendDate);
  }

  const nextDateThatNeedsToBeInvoiced = getDateOfNextInvoice(
    startDate,
    lastInvoiceDate,
    paymentFrequency as LeasePaymentFrequency,
    paymentInvoicePeriod as LeasePaymentInvoicePeriod,
    paymentInvoicePeriodCustomDay
  );

  const afterLastInvoice = isAfter(nextDateThatNeedsToBeInvoiced, lastLeaseInvoiceDate);

  if (afterLastInvoice) {
    return false;
  }

  const effectiveDueDate = computeInvoiceDueDate(nextDateThatNeedsToBeInvoiced, invoiceDaysAfterPeriodStart);

  const dateWhenNextInvoiceShouldBeSent = capDraftDateToLastRun(
    subtractCalendarDate(effectiveDueDate, leadDaysBeforeDueDate, 'days'),
    endDate,
    phase
  );

  return isAfterOrEqual(date, dateWhenNextInvoiceShouldBeSent);
};

/**
 * Returns the date of the next invoice based on the latest invoice date and configuration
 * It will return the day of month for the month concerned.
 * Eg: If set to be invoiced the 31, we will return the 28 for february.
 */
export const getDateOfNextInvoice = (
  startDate: CalendarDate,
  leaseLastInvoiceDate: CalendarDate | null | undefined,
  paymentFrequency: LeasePaymentFrequency | keyof typeof LeasePaymentFrequency,
  paymentInvoicePeriod: LeasePaymentInvoicePeriod,
  paymentInvoicePeriodCustomDay: number | null | undefined
): CalendarDate => {
  if (!leaseLastInvoiceDate) {
    return startDate;
  }

  const startPeriodDay = getStartPeriodDay(
    leaseLastInvoiceDate,
    paymentInvoicePeriod,
    paymentInvoicePeriodCustomDay || undefined,
    paymentFrequency as LeasePaymentFrequency
  );

  const startDayOfMonth = getStartDayForInvoicePeriod(
    startPeriodDay,
    paymentInvoicePeriod,
    paymentInvoicePeriodCustomDay || undefined,
    paymentFrequency as LeasePaymentFrequency
  );

  return getNextStartDate(startPeriodDay, startDayOfMonth, paymentFrequency as LeasePaymentFrequency);
};

/**
 * Returns the last invoice date given the next invoice date and configuration.
 * This is the inverse of getDateOfNextInvoice.
 */
export const getLastInvoiceDateFromNextInvoiceDate = (
  nextInvoiceDate: CalendarDate,
  paymentFrequency: LeasePaymentFrequency | keyof typeof LeasePaymentFrequency,
  paymentInvoicePeriod: LeasePaymentInvoicePeriod,
  paymentInvoicePeriodCustomDay: number | null | undefined
): CalendarDate => {
  const frequency = paymentFrequency as LeasePaymentFrequency;

  if (frequency === LeasePaymentFrequency.BI_MONTHLY) {
    return getPrevBimestrialStart(nextInvoiceDate);
  }

  const startDayOfMonth = getStartDayForInvoicePeriod(
    nextInvoiceDate,
    paymentInvoicePeriod,
    paymentInvoicePeriodCustomDay || undefined,
    frequency
  );

  const months = getNumberOfMonthsFromFrequency(frequency);
  const previousInvoiceMonth = subtractCalendarDate(nextInvoiceDate, months, 'months');

  return setCalendarDate(previousInvoiceMonth, Math.min(startDayOfMonth, previousInvoiceMonth.daysInMonth), 'day');
};

const getInvoicePeriodToBasedOnPeriodFromOnly = (
  periodFrom: CalendarDate,
  paymentFrequency: LeasePaymentFrequency,
  paymentInvoicePeriod: LeasePaymentInvoicePeriod,
  paymentInvoicePeriodCustomDay: number | null | undefined
): CalendarDate => {
  const nextStart = getNextPeriodStartDate(
    periodFrom,
    paymentInvoicePeriod,
    paymentInvoicePeriodCustomDay || undefined,
    paymentFrequency
  );

  return subtractCalendarDate(nextStart, 1, 'days');
};

/**
 * Returns the period TO endOfDay based on given periodFrom
 */
export const getInvoicePeriodTo = (
  periodFrom: CalendarDate,
  leaseEndDate: CalendarDate | undefined,
  paymentFrequency: LeasePaymentFrequency,
  paymentInvoicePeriod: LeasePaymentInvoicePeriod,
  paymentInvoicePeriodCustomDay: number | null | undefined
): CalendarDate => {
  const periodToBasedOnPeriodFrom = getInvoicePeriodToBasedOnPeriodFromOnly(
    periodFrom,
    paymentFrequency,
    paymentInvoicePeriod,
    paymentInvoicePeriodCustomDay
  );

  if (!leaseEndDate) {
    return periodToBasedOnPeriodFrom;
  }

  return isBefore(leaseEndDate, periodToBasedOnPeriodFrom) ? leaseEndDate : periodToBasedOnPeriodFrom;
};

export const isDiscount = (leaseVariousOperation: Pick<LeaseVariousOperation, 'operationType'>): boolean =>
  leaseVariousOperation.operationType === LeaseVariousOperationType.DISCOUNT;

export const getNumberOfMonthsFromFrequency = (
  invoiceFrequency: LeasePaymentFrequency | keyof typeof LeasePaymentFrequency
) => {
  switch (invoiceFrequency) {
    case LeasePaymentFrequency.BI_MONTHLY:
      return 0.5;
    case LeasePaymentFrequency.MONTHLY:
      return 1;
    case LeasePaymentFrequency.QUARTERLY:
      return 3;
    case LeasePaymentFrequency.BI_ANNUAL:
      return 6;
    case LeasePaymentFrequency.YEARLY:
      return 12;
    default:
      return 0;
  }
};

export const isInvoicePeriodCustomAndValid = (
  paymentInvoicePeriod?: LeasePaymentInvoicePeriod,
  paymentInvoicePeriodCustomDay?: number | null
): boolean => {
  return (
    paymentInvoicePeriod === LeasePaymentInvoicePeriod.CUSTOM_PERIOD &&
    !isNil(paymentInvoicePeriodCustomDay) &&
    !isNaN(Number(paymentInvoicePeriodCustomDay))
  );
};

export const isTodayTheInvoiceDay = (
  lease: Lease | LeaseDTO,
  date: CalendarDate,
  daysBeforeDueDateSetting: number
): boolean => {
  const dayAfterDueDateSetting = addCalendarDate(date, daysBeforeDueDateSetting, 'days');

  switch (lease.paymentInvoicePeriod) {
    case LeasePaymentInvoicePeriod.CUSTOM_PERIOD: {
      const paymentDueDateValue = Number(lease.paymentInvoicePeriodCustomDay);
      const safeCustomValue = Math.min(paymentDueDateValue, dayAfterDueDateSetting.daysInMonth);
      return safeCustomValue === dayAfterDueDateSetting.day;
    }
    case LeasePaymentInvoicePeriod.CONTRACT_PERIOD: {
      const contractDay = toCalendarDate(lease.startDate!).day;
      const safeDayValue = Math.min(contractDay, dayAfterDueDateSetting.daysInMonth);
      return safeDayValue === dayAfterDueDateSetting.day;
    }
    case LeasePaymentInvoicePeriod.CALENDAR_PERIOD:
      return dayAfterDueDateSetting.day === 1;
    default:
      return false;
  }
};

export const hasLeaseBeenInvoicedThisMonth = (
  lease: Lease,
  startOfMonth: CalendarDate,
  daysBeforeDueDateSetting: number
) => {
  if (!lease.lastInvoiceDate) return false;

  const thisMonthDueDate = getLeaseMonthDueDate(
    toCalendarDate(lease.startDate),
    lease.paymentInvoicePeriod as LeasePaymentInvoicePeriod,
    lease.paymentInvoicePeriodCustomDay,
    startOfMonth
  );
  const thisMonthInvoicedDate = thisMonthDueDate.subtract({
    days: daysBeforeDueDateSetting,
  });

  return isAfter(toCalendarDate(lease.lastInvoiceDate), thisMonthInvoicedDate);
};

export const computeInvoiceDueDate = (periodFrom: CalendarDate, daysAfterPeriodStart?: number | null): CalendarDate => {
  if (!daysAfterPeriodStart) return periodFrom;
  return addCalendarDate(periodFrom, daysAfterPeriodStart, 'days');
};

export interface InvoiceDates {
  dueDate: CalendarDate;
  invoiceDate: CalendarDate;
}

export const computeInvoiceDates = (
  periodFrom: CalendarDate,
  invoiceDaysAfterPeriodStart: number | null | undefined,
  daysBeforeDueDate: number
): InvoiceDates => {
  const dueDate = computeInvoiceDueDate(periodFrom, invoiceDaysAfterPeriodStart);
  return { dueDate, invoiceDate: subtractCalendarDate(dueDate, daysBeforeDueDate, 'days') };
};

const withClampedDayOfMonth = (date: CalendarDate, day: number): CalendarDate =>
  setCalendarDate(date, Math.min(day, date.daysInMonth), 'day');

export const getLeaseMonthDueDate = (
  startDate: CalendarDate,
  paymentInvoicePeriod: LeasePaymentInvoicePeriod,
  paymentInvoicePeriodCustomDay: number | null | undefined,
  startOfMonth: CalendarDate
): CalendarDate => {
  if (isInvoicePeriodCustomAndValid(paymentInvoicePeriod, paymentInvoicePeriodCustomDay)) {
    return withClampedDayOfMonth(startOfMonth, Number(paymentInvoicePeriodCustomDay));
  }

  if (paymentInvoicePeriod === LeasePaymentInvoicePeriod.CONTRACT_PERIOD) {
    return withClampedDayOfMonth(startOfMonth, startDate.day);
  }

  const result = isSameMonthAndYear(startDate, startOfMonth) ? startDate : startOfMonth;
  return result;
};

export const getLeaseNextDueDate = (lease: Lease, date: CalendarDate = today()): CalendarDate =>
  getDateOfNextInvoice(
    toCalendarDate(lease.startDate),
    date,
    lease.paymentFrequency as LeasePaymentFrequency,
    lease.paymentInvoicePeriod as LeasePaymentInvoicePeriod,
    lease.paymentInvoicePeriodCustomDay
  );

export const getLeaseExtendedName = (
  lease: Lease | LeaseDTO | undefined | null,
  language: Language = Language.EN
): string => {
  if (!lease) {
    return '';
  }

  const leaseName = lease.name ?? '';
  const formattedStartDate = formatCalendarDate(toCalendarDate(lease.startDate), language);

  return `${leaseName}    ${formattedStartDate}`;
};

export const getLeaseName = (lease?: LeaseDTO | null) => lease?.name ?? '';
/**
 * Filter the list of leases to keep only those that lie in the extended interval
 *  i.e. [periodFrom -1 year, periodTo + 1 month].
 * This interval allows to fetch leases that were paid too late or too early.
 */
export const keepLeases1YearBeforePeriodFromAnd1MonthAfterPeriodTo = (
  unitLeases: UnitLease[] | UnitLeaseDTO[],
  periodFrom: CalendarDate,
  periodTo: CalendarDate
): UnitLease[] => {
  const extendedInterval: Interval = {
    start: subtractCalendarDate(periodFrom, 1, 'years'),
    end: addCalendarDate(periodTo, 1, 'months'),
  };

  return (unitLeases as UnitLease[]).filter(
    (unitLease) =>
      unitLease.lease &&
      areIntervalsOverlapping(extendedInterval, {
        start: toCalendarDate(unitLease.lease.startDate),
        end: toCalendarDate(unitLease.lease.endDate),
      })
  );
};

export const isLeaseFixedPriceForUnit = (lease: Lease | LeaseDTO, unitId: string) => {
  const lvoItems: LeaseVariousOperation[] = !isNil(lease.variousOperations)
    ? 'items' in lease.variousOperations
      ? (lease.variousOperations.items as LeaseVariousOperation[])
      : (lease.variousOperations as unknown as LeaseVariousOperation[])
    : [];

  return (lvoItems ?? []).some((lvo) => lvo && isFixedChargeLvoForUnit(lvo, unitId));
};

export const getRentOfAllUnitsWithNoFurnitureRent = (units: UnitLease[] | null | undefined) => {
  if (isNil(units)) return 0;
  const totalRent = units.reduce((sum, currentUnit) => {
    if (currentUnit.furnishedRental && !isNil(currentUnit.furnishedRentalRentalPricePercentage)) {
      const rentWithoutFurniture =
        currentUnit.rentalPrice * ((100 - currentUnit.furnishedRentalRentalPricePercentage) / 100);

      return sum + rentWithoutFurniture;
    }
    return sum + currentUnit.rentalPrice;
  }, 0);
  return totalRent;
};

export const getFurnishedRentOfAllUnitsWithFurnitureRent = (units: UnitLease[] | null | undefined) => {
  if (isNil(units)) return 0;
  const totalRent = units.reduce((sum, currentUnit) => {
    if (currentUnit.furnishedRental && !isNil(currentUnit.furnishedRentalRentalPricePercentage)) {
      const furnitureRentPart =
        currentUnit.rentalPrice -
        currentUnit.rentalPrice * ((100 - currentUnit.furnishedRentalRentalPricePercentage) / 100);
      return sum + furnitureRentPart;
    }
    return sum;
  }, 0);
  return totalRent;
};

export const getAllMonthlyProvisionsForUnits = (
  units: UnitLease[] | null | undefined,
  leaseVariousOperations: LeaseVariousOperation[] = []
) => {
  if (isNil(units)) return 0;
  return units.reduce((sum, currentUnit) => {
    const provisionedAmount = leaseVariousOperations.reduce(
      (amount, lvo) => (isProvisionedChargeLvoForUnit(lvo, currentUnit.unitId) ? amount + lvo.amount : amount),
      0
    );
    return sum + provisionedAmount;
  }, 0);
};

export const getAllMonthlyFixedCharges = (
  units: UnitLease[] | null | undefined,
  leaseVariousOperations: LeaseVariousOperation[] = []
) => {
  if (isNil(units)) return 0;
  return units.reduce((sum, currentUnit) => {
    const fixedAmount = leaseVariousOperations.reduce(
      (amount, lvo) => (isFixedChargeLvoForUnit(lvo, currentUnit.unitId) ? amount + lvo.amount : amount),
      0
    );
    return sum + fixedAmount;
  }, 0);
};

export const getLeaseMainUnit = (lease: LeaseExtended | undefined, getUnit: (id: string) => Unit | undefined) => {
  if (isNil(lease)) return undefined;
  const unitLease = (lease?.units ?? []).find((unitLeaseElement) => {
    return unitLeaseElement.mainUnit;
  });
  const mainUnit = getUnit(unitLease?.unit?.id ?? '');
  return mainUnit;
};

export const getUnitRentalPrice = (unit: UnitLease | null | undefined) => {
  if (isNil(unit)) return { furnishedRental: 0, actualRentalPrice: 0 };
  const furnishedRental =
    unit.furnishedRental && !isNil(unit.furnishedRentalRentalPricePercentage)
      ? unit.rentalPrice * (1 - unit.furnishedRentalRentalPricePercentage)
      : 0;

  const actualRentalPrice = unit.rentalPrice - furnishedRental;

  const monthlyProvisions = getAllMonthlyProvisionsForUnits([unit]);
  const monthlyFixedCharges = getAllMonthlyFixedCharges([unit]);

  return { furnishedRental, actualRentalPrice, monthlyProvisions, monthlyFixedCharges };
};

export const getUnitsOfLeaseExtended = (lease: LeaseExtended): Unit[] =>
  (orderBy(lease.units, 'mainUnit', 'desc')
    .map((unitLease) => unitLease.unit)
    .filter((unit) => unit) as Unit[]) ?? [];

export const getPriceForFrequency = (lease: Lease, totalPrice: number) => {
  return getPriceForPaymentFrequency(lease.paymentFrequency ?? LeasePaymentFrequency.MONTHLY, totalPrice);
};

export const getPriceForPaymentFrequency = (paymentFrequency: LeasePaymentFrequency, totalPrice: number) => {
  const amountOfMonthsFrequency = getNumberOfMonthsFromFrequency(paymentFrequency);

  return roundForFrequencyPrice(totalPrice, amountOfMonthsFrequency);
};

export const hasLeaseAlreadyBeenEnded = (leaseActionHistories: LeaseActionHistory[]) => {
  return leaseActionHistories.some((leaseActionHistory) => {
    return leaseActionHistory.action === LeaseAction.END_LEASE;
  });
};

export const isEndLeaseActionAmendable = (
  lease: Lease | LeaseExtended | LeaseDTO,
  endLeaseAction: LeaseActionHistory,
  hasTerminationLetterLeftThePlatform: boolean = false
) => {
  if (lease.status !== LeaseStatus.Active) {
    return false;
  }
  const indemnityAlreadyInvoiced =
    !isNil(endLeaseAction.totalAmount) &&
    endLeaseAction.totalAmount !== 0 &&
    endLeaseAction.status === LeaseActionHistoryStatus.PROCESSED;
  if (indemnityAlreadyInvoiced) {
    return false;
  }
  return !hasTerminationLetterLeftThePlatform;
};

export const getUnitLeasesFromUnitOrDTO = (unit: Unit | UnitDTO) => {
  if (isNilOrEmpty(unit.leases)) {
    return [];
  }
  const cleanedList = removeDeletedItemsInObject(unit.leases);
  if ('items' in cleanedList) {
    return (cleanedList as ModelUnitLeaseConnection).items as UnitLeaseDTO[];
  }
  return cleanedList as UnitLeaseDTO[];
};

export const getAddressIdOfLease = (lease: LeaseDTO) => {
  const contacts = (lease.contacts as ModelLeaseContactConnection).items;
  const mainContact = contacts?.[0]?.contact;
  return mainContact?.addressId ?? '';
};

export const LEASES_IN_SETUP_STATUSES: LeaseStatus[] = [
  LeaseStatus.Rejected,
  LeaseStatus.Draft,
  LeaseStatus.OutForSignature,
];

const isLeaseActive = (lease: Lease | LeaseExtended | LeaseDTO) => lease.status === LeaseStatus.Active;

const isLeaseAlreadyStopped = (type: LeaseType, endDate?: CalendarDate): boolean =>
  type !== LeaseType.MAIN_RESIDENCE_INFINITE && !!endDate && isBefore(endDate, today());

export const getLeaseStatusForAddEdit = (
  type: LeaseType,
  hasContractFiles: boolean,
  continueWithNoFilesAndPutLater: boolean,
  needToRedirectToEditSignatures: boolean,
  endDate?: CalendarDate
): LeaseStatus => {
  const shouldStayDraft =
    (!hasContractFiles && !continueWithNoFilesAndPutLater) || (hasContractFiles && needToRedirectToEditSignatures);

  if (shouldStayDraft) {
    return LeaseStatus.Draft;
  }

  return isLeaseAlreadyStopped(type, endDate) ? LeaseStatus.Ended : LeaseStatus.Active;
};

/**
 * Whether recording a termination ends the lease at once: an active lease whose new end date is already past,
 * instead of waiting for the nightly lease scheduler.
 */
export const shouldEndLeaseOnTermination = (status: LeaseStatus, newEndDate: CalendarDate): boolean =>
  status === LeaseStatus.Active && isBefore(newEndDate, today());

export const getActiveLeases = (leases: Lease[] | LeaseExtended[] | LeaseDTO[]) =>
  leases.filter((lease) => isLeaseActive(lease));

export const getActiveLeasesFromPartialLeases = (
  partialLeases: UnitLease[],
  getLease: (id: string) => LeaseExtended | null | undefined
) =>
  partialLeases.reduce((acc, partialLease) => {
    const lease = getLease(partialLease.leaseId)!;
    if (isLeaseActive(lease)) {
      acc.push(lease);
    }
    return acc;
  }, [] as LeaseExtended[]);

export const filterLeasesByOwnerId = (
  leases: LeaseExtended[],
  buildings: Building[],
  units: Unit[],
  ownerId: string
) => {
  const allOwnerBuildings = buildings.filter((building) =>
    building.owners?.some((buildingOwner) => buildingOwner.contactId === ownerId)
  );
  const allOwnerUnitsFromUnits = units.filter((unit) =>
    unit?.owners?.some((unitOwner) => unitOwner.ownerId === ownerId)
  );
  const allOwnerUnits = [...allOwnerUnitsFromUnits, ...allOwnerBuildings.flatMap((building) => building.units)];
  const allOwnerUnitsIds = allOwnerUnits.map((unit) => unit?.id);

  return leases.filter((lease) => {
    const isOwnerTheLessor = lease.contacts?.some(
      (leaseContact) =>
        [ContactType.OWNER, ContactType.JOINT_OWNERSHIP].includes(leaseContact.contactRole) &&
        leaseContact.contactId === ownerId
    );
    if (isOwnerTheLessor) {
      return true;
    }

    const isUnitOrBuildingOwner = lease.units?.some((unitLease) => allOwnerUnitsIds.includes(unitLease.unitId));
    return isUnitOrBuildingOwner;
  });
};

export const isLeaseIncludedInManagementFilters = (lease: Lease, managementFilters: FilterGroup[]) => {
  const unitIdsToShow = getFilterEntityValuesForGroups(managementFilters, FilterEntityType.UNIT, 'id');
  const unitsFromLease = (lease.units ?? []).map((unit) => unit.unitId);
  return unitIdsToShow.some((unitIdToShow) => unitsFromLease.includes(unitIdToShow));
};

export const isLeaseRentVatLiable = (lease: Lease | LeaseDTO): boolean => !isNil(lease.vatRateRentId);

export const hasLeaseChargeWithVat = (lease: Lease | LeaseDTO): boolean => {
  const lvoItems: LeaseVariousOperation[] = !isNil(lease.variousOperations)
    ? 'items' in lease.variousOperations
      ? (lease.variousOperations.items as LeaseVariousOperation[])
      : (lease.variousOperations as unknown as LeaseVariousOperation[])
    : [];
  return lvoItems.some((lvo) => !isNil(lvo) && !isNilOrEmpty(lvo.vatRateId) && isChargeLvo(lvo));
};

export const isLeaseVatLiable = (lease: Lease | LeaseDTO): boolean =>
  hasLeaseChargeWithVat(lease) || isLeaseRentVatLiable(lease);

export const hasVatPosting = (postings: Pick<Posting, 'vatRateId'>[]): boolean =>
  postings.some((posting) => !isNilOrEmpty(posting.vatRateId));

export const isLeaseMandateActive = (lease: Pick<LeaseDTO, 'paymentOption'> | Pick<Lease, 'paymentOption'>) =>
  lease.paymentOption?.enabled === true &&
  (lease.paymentOption.mandateStatus === PaymentProviderMandateStatus.ACTIVE ||
    !isNilOrEmpty(lease.paymentOption.mandateActivatedAt));
